import { useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'next-i18next';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper/types';
import { Thumbs, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';
import styles from '@features/productPreview/sections/ProductDescription/components/ProductDescriptionBlock/ProductTechnicalPictureGallery.styles';
import { SLIDER_CONTAINER_HEIGHT, THUMB_IMG_HEIGHT } from '@features/productPreview/const';
import { ContentTechnicalPhotoItemType, PhotoSizeType } from '@features/productPreview/sections/ProductDescription/typescript';
import { uiState } from '@features/productPreview/mobX/state/context';
import Icon from '@components/icons/Icon';
import Button from '@components/Button/Button';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';
type PropsType = {
  slides: ContentTechnicalPhotoItemType[];
  setIsOpenGallery: (active: boolean) => void;
};
const ProductTechnicalPictureGallery = ({
  slides,
  setIsOpenGallery
}: PropsType) => {
  const {
    windowDimensions,
    selectedTechSlider,
    productName
  } = uiState;
  const {
    t
  } = useTranslation('common');
  const pictureAlt = [productName, t('accessibility.technicalDrawing')].filter(Boolean).join(' ');
  const windowHeight = windowDimensions.height;
  const windowWidth = windowDimensions.width;
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | undefined>(undefined);
  const availableHeight = windowHeight - SLIDER_CONTAINER_HEIGHT;
  const swipedThumbsInstance = useRef<SwiperClass>(null);
  const [arrowsVisible, setArrowsVisible] = useState<boolean>(false);
  if (!selectedTechSlider) {
    return null;
  }
  const onNextClick = () => {
    if (!swipedThumbsInstance) return;
    swipedThumbsInstance.current?.slideNext();
  };
  const onPrevClick = () => {
    if (!swipedThumbsInstance) return;
    swipedThumbsInstance.current?.slidePrev();
  };
  const renderArrows = (swiper: SwiperClass) => {
    const fullImgContainerPadding = 300;
    if (windowWidth - fullImgContainerPadding < swiper.width) {
      setArrowsVisible(true);
    } else {
      setArrowsVisible(false);
    }
  };
  return <div sx={styles.fullModeContainer} data-cy="ProductTechnicalPictureGallery">
      <div sx={styles.fullModeContentContainer}>
        <Button variant="iconOnly" onClick={() => setIsOpenGallery(false)} cy="CloseButton" addSx={styles.closeButton}>
          <Icon icon="Close2" size={['16px', '16px', '21px', '21px', '24px']} />
        </Button>
        <div sx={styles.fullModeImg}>
          <Swiper slidesPerView={1} thumbs={{
          swiper: swipedThumbsInstance.current?.destroyed ? undefined : swipedThumbsInstance.current || thumbsSwiper
        }} navigation modules={[Thumbs, Navigation]} className="mySwiper2" style={{
          // @ts-ignore
          '--swiper-navigation-color': '#fff'
        }}>
            {slides.map((slider, index) => <SwiperSlide key={slider.id}>
                <div sx={styles.sliderImg}>
                  <ResponsiveImage alt={slides.length > 1 ? `${pictureAlt} ${index + 1}` : pictureAlt} image={slider.sizes[PhotoSizeType.MEDIUM]} width={availableHeight} height={availableHeight} objectFit="contain" objectPosition="center" dataCy="PictureGalleryImage" />
                </div>
              </SwiperSlide>)}
          </Swiper>
        </div>

        <div sx={styles.sliderContainer}>
          <Swiper spaceBetween={24} slidesPerView="auto" pagination={{
          clickable: true
        }} onSwiper={swiper => {
          swipedThumbsInstance.current = swiper;
          setThumbsSwiper(swiper);
        }} modules={[Thumbs]} onAfterInit={renderArrows} onResize={renderArrows} className="mySwiper2" data-cy="Swiper">
            {slides.map((slider, index) => <SwiperSlide key={slider.id}>
                <div sx={styles.sliderThumb}>
                  <ResponsiveImage alt={slides.length > 1 ? `${pictureAlt} ${index + 1}` : pictureAlt} image={slider.sizes[PhotoSizeType.THUMBNAIL] || slider.sizes[PhotoSizeType.MEDIUM]} width={THUMB_IMG_HEIGHT} height={THUMB_IMG_HEIGHT} objectFit="cover" objectPosition="center" />
                </div>
              </SwiperSlide>)}
          </Swiper>
        </div>
        {arrowsVisible && <div>
            <Button variant="iconOnly" onClick={onPrevClick} addSx={styles.sliderArrowButton('left')} cy="ArrowLeft">
              <Icon icon="ChevronLeft" size="46px" ariaHidden />
            </Button>
            <Button variant="iconOnly" onClick={onNextClick} addSx={styles.sliderArrowButton('right')} cy="ArrowRight">
              <Icon icon="ChevronRight" size="46px" ariaHidden />
            </Button>
          </div>}
      </div>
    </div>;
};
export default observer(ProductTechnicalPictureGallery);